import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import repositoryContainer from '../../services/RepositoryContainer';
import { getCompanyScopeFromPath, getCompanyScopedStorageKey } from '../../utils/companyScope';

const CompanyContext = createContext(null);

export const CompanyProvider = ({ children }) => {
  const location = useLocation();
  const companyScope = useMemo(() => getCompanyScopeFromPath(location.pathname), [location.pathname]);
  const companyPath = companyScope === 'global' ? null : companyScope;
  const storageKey = useMemo(
    () => getCompanyScopedStorageKey('company', location.pathname),
    [location.pathname]
  );

  const [company, setCompany] = useState(null);
  const [isLoading, setIsLoading] = useState(Boolean(companyPath));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyPath) {
      setCompany(null);
      setError(null);
      setIsLoading(false);
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    try {
      const cachedCompany = window.localStorage.getItem(storageKey);
      setCompany(cachedCompany ? JSON.parse(cachedCompany) : null);
    } catch (cacheError) {
      console.warn('Failed to restore cached company:', cacheError);
      setCompany(null);
    }
  }, [companyPath, storageKey]);

  const fetchCompany = async (path) => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await repositoryContainer.company.getByPath(path);
      setCompany(data);

      if (typeof window !== 'undefined' && path) {
        const companyStorageKey = getCompanyScopedStorageKey('company', `/${path}`);
        window.localStorage.setItem(companyStorageKey, JSON.stringify(data));
      }

      return data;
    } catch (err) {
      setError(err.data?.message || 'Company not found');
      setCompany(null);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CompanyContext.Provider value={{ company, companyPath, isLoading, error, fetchCompany }}>
      {children}
    </CompanyContext.Provider>
  );
};

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};